/**
 * plugins/titles.ts — Títulos e subtítulos (h1-h4)
 *
 * Ferramentas: editar texto, âncoras (IDs), badge associado.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';

export class TitlesPlugin implements VtbPlugin {
    readonly panelId = 'vtb-titles';
    readonly selectorHints = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(e.tagName) || 
            ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(e.dataset.vtbId || '')
        );
        
        if (!el) return null;
        
        return { type: this.panelId, label: 'Título', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;
        const textIn = registry.q<HTMLInputElement>('vtb-title-text');
        const idIn   = registry.q<HTMLInputElement>('vtb-title-id');

        if (textIn) {
            // Se houver badge, pega só o texto, senão textContent
            const badge = el.querySelector('.badge');
            if (badge) {
                // Clone para não afetar o original ao extrair texto
                const temp = el.cloneNode(true) as HTMLElement;
                temp.querySelector('.badge')?.remove();
                textIn.value = temp.textContent?.trim() || '';
            } else {
                textIn.value = el.textContent?.trim() || '';
            }
        }

        if (idIn) {
            idIn.value = el.id || '';
        }

        // Atualiza o chip com a tag real
        const chip = registry.q('vtb-titles .vtb-chip');
        if (chip) chip.textContent = el.tagName;
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-title-apply')?.addEventListener('click', () => this._apply(registry));
        registry.q('vtb-title-text')?.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') this._apply(registry);
        });
    }

    private _apply(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        
        const text = registry.q<HTMLInputElement>('vtb-title-text')?.value?.trim();
        const id   = registry.q<HTMLInputElement>('vtb-title-id')?.value?.trim();
        
        if (text !== undefined) {
            const badge = ctx.el.querySelector('.badge');
            if (badge) {
                // Preserva o badge e muda o texto
                // O texto costuma vir após o badge ou antes
                // Simplificando: vamos manter o badge e setar o text content no nó de texto
                const nodes = Array.from(ctx.el.childNodes);
                const textNode = nodes.find(n => n.nodeType === Node.TEXT_NODE);
                if (textNode) {
                    textNode.textContent = ' ' + text;
                } else {
                    ctx.el.appendChild(document.createTextNode(' ' + text));
                }
            } else {
                ctx.el.textContent = text;
            }
        }

        if (id !== undefined) {
            if (id) ctx.el.id = id;
            else ctx.el.removeAttribute('id');
        }
        
        registry.sync();
    }
}
